<template>
  <!-- eh-ds: stylebook primitives (brand-ds.scss). Scoped CSS is layout +
       var(--eh-*) tokens; status hues stay semantic (see STATUS_DOTS). -->
  <div class="dir-page l1 eh-ds">
    <!-- Header -->
    <header class="l1-head">
      <div>
        <h1>Directives</h1>
        <p>
          {{ rosterScopeLabel }}
          <strong>Full Code</strong> unless an NPI holder has validated a directive.
        </p>
      </div>
      <!-- No "New Patient Record" entry point: the list now holds every patient in
           the organization, so a record is started from the patient's own page
           rather than by picking them out of a search. -->
      <div
        v-if="!accessBlocked"
        class="l1-head-right"
      >
        <button
          class="l1-legend-toggle"
          @click="legendOpen = !legendOpen"
        >
          Status legend
        </button>
        <div class="l1-counts">
          <span class="l1-count"><b>{{ patients.length }}</b> patients</span>
          <span
            v-if="pendingTotal"
            class="l1-count l1-count--warn"
          >
            <b>{{ pendingTotal }}</b> awaiting sign-off
          </span>
        </div>
      </div>
    </header>

    <!-- Legend. Explains the buckets AND the count-not-severity rule, which is
         non-obvious enough that a clinician could otherwise misread the colour. -->
    <div
      v-if="!accessBlocked && legendOpen"
      class="l1-legend"
    >
      <div
        v-for="b in codeBuckets"
        :key="b.key"
        class="l1-legend-item"
      >
        <span
          class="l1-legend-dot"
          :style="{ background: CODE_COLORS[b.key].bg }"
        />
        <div>
          <div class="l1-legend-name">
            {{ b.label }}
          </div>
          <div class="l1-legend-desc">
            {{ b.note }}
          </div>
        </div>
      </div>
      <p class="l1-legend-note">
        Colour shows <strong>how many</strong> limitations are in force, not how grave they are.
        A single “Do not resuscitate” shows amber; two milder limits show red.
      </p>
    </div>

    <!-- Privilege + scope gate. Two different refusals: no privilege at all,
         or a scope we could not resolve (which fails closed rather than
         falling back to the org roster). -->
    <div
      v-if="accessBlocked"
      class="dir-empty"
    >
      <span class="glyphicon glyphicon-lock bi bi-lock-fill" />
      <h3>{{ blockedMessage }}</h3>
    </div>

    <template v-else>
      <div class="l1-tools">
        <input
          v-model.trim="search"
          class="l1-search"
          type="search"
          placeholder="Search name or MRN…"
        >
        <span class="l1-filters-label" aria-hidden="true">Quick filters</span>
        <div class="l1-filters" role="group" aria-label="Quick filters">
          <button
            v-for="f in filters"
            :key="f.key"
            class="l1-chip"
            :class="{ on: filter === f.key }"
            @click="filter = f.key"
          >
            <i
              v-if="f.dot"
              class="l1-dot"
              :style="{ background: f.dot }"
            />{{ f.label }}
          </button>
        </div>
      </div>

      <div class="l1-table">
        <div class="l1-row l1-row--head">
          <span>Code status</span>
          <span>Patient</span>
          <span>Current directive(s)</span>
          <span>Validated</span>
          <span />
        </div>

        <div
          v-for="p in pageItems"
          :key="p.patient_pid"
          class="l1-row l1-row--data"
          tabindex="0"
          @click="openPatient(p)"
          @keydown.enter="openPatient(p)"
        >
          <span class="l1-cell l1-cell--status">
            <span
              class="l1-status"
              :style="{ background: color(p).bg, color: color(p).fg }"
            >
              {{ p.code.label }}
            </span>
            <span
              v-if="p.pending"
              class="l1-pendflag"
            >{{ p.pending }} awaiting sign-off</span>
            <!-- Sits under the code status, not beside it: the record is still in
                 force, only its re-authorization has lapsed. -->
            <span
              v-if="reauthExpired(p)"
              class="l1-reauth"
              data-cy="reauth-expired-badge"
              title="An active directive record needs re-authorizing. The directives remain in force."
            >⚠ Re-auth Expired!</span>
          </span>

          <span class="l1-cell l1-cell--who">
            <b>{{ p.patient_name }}</b>
            <small>{{ p.sub }}</small>
          </span>

          <span class="l1-cell l1-cell--force">
            <em v-if="!p.code.validated">{{ p.code.because }}</em>
            <b v-else-if="p.code.directives.length === 1">{{ inForceSummary(p.code) }}</b>
            <button
              v-else
              class="l1-quick"
              @click.stop="quick = p"
              @keydown.enter.stop="quick = p"
            >
              {{ p.code.directives.length }} directives — See directives
            </button>
          </span>

          <span class="l1-cell l1-cell--by">
            <template v-if="p.code.validated">
              <b>{{ p.code.approver }}</b>
              <small>NPI {{ p.code.npi }} · {{ p.code.approvedAt }}</small>
            </template>
            <em v-else>Not validated</em>
          </span>

          <span
            class="l1-cell l1-cell--go"
            aria-hidden="true"
          >›</span>
        </div>

        <p
          v-if="loading"
          class="l1-none"
        >
          Loading the roster…
        </p>
        <p
          v-else-if="!visible.length"
          class="l1-none"
        >
          {{ emptyMessage }}
        </p>
      </div>

      <!-- Paging bounds the DOM only. `visible` — and therefore every count and
           filter chip above — still spans the whole organization, so "Awaiting
           sign-off" can never come to mean "of the page I happen to be on". -->
      <div
        v-if="!loading && visible.length"
        class="l1-pager"
      >
        <PaginationInfo
          ref="pagination-info"
          class="l1-pager-info"
          info-template="Showing {from}–{to} of {total} patients"
        />
        <Pagination
          ref="pagination"
          @pagination:change-page="goToPage"
        />
      </div>
    </template>

    <!-- Quick view: read-only, dismissible, never navigates. Keeps the list from
         reflowing underneath someone who is mid-scan. -->
    <div
      v-if="quick"
      class="l1-scrim"
      @click.self="quick = null"
    >
      <div
        class="l1-sheet"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="l1-sheet-top"
          :style="{ background: color(quick).bg, color: color(quick).fg }"
        >
          <div>
            <p class="l1-sheet-code">
              {{ quick.code.label }}
            </p>
            <p class="l1-sheet-who">
              {{ quick.patient_name }} · {{ quick.sub }}
            </p>
          </div>
          <button
            class="l1-close"
            aria-label="Close"
            @click="quick = null"
          >
            ×
          </button>
        </div>
        <ul class="l1-sheet-list">
          <li
            v-for="(label, i) in quick.code.directives"
            :key="i"
          >
            <b>{{ label }}</b>
          </li>
        </ul>
        <div class="l1-sheet-foot">
          <span v-if="quick.code.validated">
            Validated by {{ quick.code.approver }} · NPI {{ quick.code.npi }} ·
            {{ quick.code.approvedAt }}
          </span>
          <button
            class="l1-open"
            @click="openPatient(quick)"
          >
            Open patient
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { usePrivilegesStore } from "@/stores/privileges";
import { PRIVILEGES } from "@/constants/privileges";
import { getDirectivePatients, resolveDirectivePatientPid } from "@/api/directivesApi";
import { codeStatus, inForceSummary, CODE_COLORS, CODE_BUCKETS } from "./codeStatus";
import Pagination from "@/components/includes/pagination/Pagination.vue";
import PaginationInfo from "@/components/includes/pagination/PaginationInfo.vue";
import {
  getDirectiveAccountNumber,
  getDirectiveActorId,
  getSessionPatientPersonalId,
  isViewingPatientChart,
} from "@/utils/directivesSession";
import {
  resolveDirectiveScope,
  directiveGateInputsReady,
  shouldRetryDirectiveInit,
  SCOPE,
} from "@/utils/directivesScope";
import { ageInYears, formatDate } from "@/utils/dateOnly";

/** Identity line under the name. The schema carries no bed/room, and inventing
 *  one on a code-status screen would be worse than omitting it. */
function subLine(p) {
  const age = ageInYears(p.dob);
  const dob = formatDate(p.dob, "");
  return [age === null ? "" : `${age}y`, dob ? `DOB ${dob}` : "", p.mrn].filter(Boolean).join(" · ");
}

export default {
  name: "DirectivesListPage",
  components: { Pagination, PaginationInfo },
  data() {
    return {
      privilegesStore: usePrivilegesStore(),
      patients: [],
      loading: true,
      search: "",
      filter: "all",
      page: 1,
      // Larger than the app-wide default of 10: this is a scanning screen, and
      // every page break is a place a clinician has to stop and re-orient.
      perPage: 25,
      legendOpen: false,
      quick: null,
      CODE_COLORS,
      codeBuckets: CODE_BUCKETS,
      // MySQL patients.pid for a self-scoped session. Session patient.externId
      // is psersonalID, not directive_record.patient_pid — resolved async on load.
      resolvedPatientPid: "",
      // Guards the created()/watcher pair so the roster loads exactly once.
      // `initialized` means SETTLED, not "has run": a pass that failed closed
      // because the session had not arrived leaves it false, so the watcher
      // can try again once it has.
      initialized: false,
      initializing: false,
      // Resolved read scope for the acting subject. Null until created()
      // finishes; treated as blocked until then so nothing renders early.
      scope: null,
    };
  },
  computed: {
    ...mapGetters("login", { allData: "getAllData" }),
    patient() {
      return this.allData?.patient || {};
    },
    canRead() {
      return this.privilegesStore.effectiveGranted(PRIVILEGES.DIRECTIVE_READ);
    },
    viewingPatientChart() {
      return isViewingPatientChart(this.allData);
    },
    /**
     * Whether this session sees exactly one record — its own. True for a
     * patient/family/contact login AND for a staff member who has stepped into
     * a patient's chart; the two cases resolve to the same pid because
     * `allData.patient` is the subject of the chart in both.
     *
     * This deliberately does NOT key off the open-chart flag alone — that was
     * the bug: a directly-logged-in patient has no chart open, so every guard
     * below was skipped for them.
     */
    selfScoped() {
      return this.viewingPatientChart || (this.scope && this.scope.kind === SCOPE.SELF);
    },
    /** Roster limited to this caregiver's panel. */
    assignmentScoped() {
      return !!(this.scope && this.scope.kind === SCOPE.ASSIGNED);
    },
    /** The subheading must not promise the org roster to a scoped session. */
    rosterScopeLabel() {
      return this.assignmentScoped
        ? "The patients assigned to you."
        : "Every patient in your organization.";
    },
    /** Fail closed: no privilege, or a scope we could not resolve. */
    accessBlocked() {
      if (!this.canRead) return true;
      return !this.scope || this.scope.kind === SCOPE.NONE;
    },
    blockedMessage() {
      if (!this.canRead) return "You do not have permission to view directives.";
      return "We could not confirm which records you may view.";
    },
    filters() {
      return [
        { key: "all", label: "All", dot: "" },
        { key: "limits_many", label: "Care limitations (2+)", dot: CODE_COLORS.limits_many.bg },
        { key: "limits_one", label: "Care limitations (1)", dot: CODE_COLORS.limits_one.bg },
        { key: "full", label: "Full Code", dot: CODE_COLORS.full.bg },
        { key: "pending", label: "Awaiting sign-off", dot: "#5c666f" },
        { key: "reauth", label: "Re-auth expired", dot: "#c0392b" },
      ];
    },
    pendingTotal() {
      return this.patients.reduce((n, p) => n + p.pending, 0);
    },
    // An empty list has several distinct causes and they are not
    // interchangeable. A filter that matches nobody is good news — nobody is
    // awaiting sign-off — whereas a search that matches nobody means "look
    // again". Naming the search term when there isn't one produced the nonsense
    // `No patients match “”.`
    //
    // An assignment-scoped session with nothing to show is different again: the
    // org is not empty, the caregiver simply has no panel yet. Saying "no
    // patients in this organization" there is actively misleading, and it is
    // the state every nurse, allied professional, home health aid and GCM lands
    // in before an administrator assigns anyone to them.
    emptyMessage() {
      const chip = this.filters.find((f) => f.key === this.filter);
      if (this.search && this.filter !== "all") {
        return `No patients under “${chip.label}” match “${this.search}”.`;
      }
      if (this.search) return `No patients match “${this.search}”.`;
      if (this.filter !== "all") return `No patients under “${chip.label}”.`;
      if (this.assignmentScoped) {
        return "No patients are assigned to you yet. Ask an administrator to assign patients to you, and their directives will appear here.";
      }
      return "No patients in this organization yet.";
    },
    visible() {
      const q = this.search.toLowerCase();
      return this.patients.filter((p) => {
        const hit = !q || `${p.patient_name} ${p.sub}`.toLowerCase().includes(q);
        let f = true;
        if (this.filter === "pending") f = p.pending > 0;
        else if (this.filter === "reauth") f = this.reauthExpired(p);
        else if (this.filter !== "all") f = p.code.key === this.filter;
        return hit && f;
      });
    },
    totalPage() {
      return Math.max(1, Math.ceil(this.visible.length / this.perPage));
    },
    /** The slice actually rendered. Everything else reads `visible`. */
    pageItems() {
      const start = this.perPage * (this.page - 1);
      return this.visible.slice(start, start + this.perPage);
    },
  },
  watch: {
    // Narrowing the list can strand you past the last page — searching from
    // page 4 would otherwise show an empty table rather than the matches.
    visible() {
      if (this.page > this.totalPage) this.page = 1;
      this.syncPagination();
    },
    page() {
      this.syncPagination();
    },
    /**
     * A hard reload resets the app-level FSM and Vuex has not rehydrated the
     * session by the time `created()` runs, so the gate resolves against an
     * empty object and fails closed — and `created()` never runs again, leaving
     * a caregiver staring at an empty roster with their patients still
     * assigned.
     *
     * Re-run once the inputs actually land. Both of them are watched, because
     * either can be the late one and the privileges store is the one that
     * usually is (see shouldRetryDirectiveInit); a genuine refusal stays
     * refused.
     */
    allData: {
      handler() {
        this.retryInitIfUnsettled();
      },
      deep: false,
    },
    "privilegesStore.loadedKey": function onPrivilegesLoaded() {
      this.retryInitIfUnsettled();
    },
  },
  async created() {
    await this.runInit();
  },
  methods: {
    /**
     * One entry point for both `created()` and the watchers, so a pass already
     * in flight is never doubled — it re-reads both inputs after each `await`
     * and will pick up whatever landed mid-flight on its own.
     */
    async runInit() {
      if (this.initialized || this.initializing) return;
      this.initializing = true;
      try {
        await this.init();
      } catch (e) {
        // Nothing below is allowed to strand the page in its loading state:
        // `loading` is what the spinner reads, and no one else clears it.
        console.error("[directives] init failed", e);
        this.loading = false;
      } finally {
        this.initializing = false;
      }
    },
    retryInitIfUnsettled() {
      const retry = shouldRetryDirectiveInit({
        initialized: this.initialized,
        initializing: this.initializing,
        allData: this.allData,
        privilegesStore: this.privilegesStore,
      });
      if (retry) this.runInit();
    },
    /**
     * Resolve scope and load the roster. Split out of `created()` so the
     * `allData` watcher can re-run it after a hard reload, and guarded by
     * `initialized` so the two entry points cannot both load.
     */
    async init() {
      if (this.initialized) return;
    // Privileges may already be loaded by the session sync; ensure they're present.
    if (this.allData && (!this.privilegesStore.loadedKey || this.privilegesStore.assignments.length === 0)) {
      try {
        await this.privilegesStore.fetchForCurrentSession(this.allData);
      } catch (e) {
        // non-fatal; gate simply stays closed
        console.warn("[directives] privilege load failed", e);
      }
    }
    // Resolve read scope from the ACTOR before any data is fetched. Must come
    // after the privilege load above: the "Administrators" group check reads
    // the store.
    try {
      this.scope = resolveDirectiveScope(this.allData, this.privilegesStore);
    } catch (e) {
      console.warn("[directives] scope resolution failed", e);
      this.scope = { kind: SCOPE.NONE, reason: "error" };
    }

    // Nothing is fetched without both a privilege and a resolved scope. The
    // previous version gated only the template and called loadPatients()
    // unconditionally, so the org roster was retrieved even when the UI
    // refused to draw it.
    if (this.accessBlocked) {
      // Settled only if the gate had something to go on. With both inputs
      // present this is a real answer and must not be retried every time the
      // session object changes identity; without them it is a startup race,
      // and marking it settled is what left this roster permanently empty.
      this.initialized = directiveGateInputsReady(this.allData, this.privilegesStore);
      this.loading = false;
      return;
    }

    // Self-scoped sessions skip the org list entirely: resolve their MySQL pid
    // from accountnumber + psersonalID and land on their own detail page. This
    // covers both a patient logging in directly and a staff member who has
    // stepped into a chart.
    if (this.selfScoped) {
      this.resolvedPatientPid = await resolveDirectivePatientPid({
        accountnumber: getDirectiveAccountNumber(this.allData),
        psersonalId: getSessionPatientPersonalId(this.patient),
      });
      if (this.resolvedPatientPid) {
        this.initialized = true;
        this.$router.replace("/directives/" + this.resolvedPatientPid);
        return;
      }
      // No pid to scope TO. For a caregiver that is expected — they are not a
      // patient — so they fall to ASSIGNED and the server returns their panel.
      // Falling through to an UNFILTERED org roster is what caused the leak;
      // that cannot happen now, because the roster endpoint requires an actor
      // and filters by the same three rules.
      this.scope = { kind: SCOPE.ASSIGNED };
    }
    this.initialized = true;
    await this.loadPatients();
    },
    inForceSummary,
    /**
     * The shared pagination components take their state imperatively via
     * `setPaginationData` on a ref (see FilterableSortablePage), so it has to be
     * pushed after every change rather than bound. Both refs sit behind
     * `v-if`s, hence the nextTick and the guards.
     */
    syncPagination() {
      this.$nextTick(() => {
        const data = {
          currentPage: this.page,
          totalPage: this.totalPage,
          total: this.visible.length,
          from: this.perPage * (this.page - 1) + 1,
          to: Math.min(this.visible.length, this.perPage * this.page),
        };
        if (this.$refs.pagination) this.$refs.pagination.setPaginationData(data);
        if (this.$refs["pagination-info"]) {
          this.$refs["pagination-info"].setPaginationData(data);
        }
      });
    },
    goToPage(page) {
      this.page = Math.min(Math.max(1, page), this.totalPage);
      // A page change is a jump, not a scroll — land at the top of the table
      // rather than halfway down the new page.
      if (this.$el && this.$el.scrollIntoView) {
        this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    async loadPatients() {
      this.loading = true;
      try {
        let patients = await getDirectivePatients({
          accountnumber: getDirectiveAccountNumber(this.allData),
          actorId: getDirectiveActorId(this.allData),
        });
        // Defensive: if the self-scope redirect did not run, still narrow the
        // list to the resolved MySQL pid (not session externId/id). A
        // self-scoped session with no resolved pid gets nothing — never the
        // unfiltered roster.
        if (this.selfScoped) {
          const pid = this.resolvedPatientPid;
          patients = pid ? patients.filter((p) => String(p.patient_pid) === pid) : [];
        }
        // Code status is derived once, here — every cell, chip and filter reads
        // the same object, so the table can never disagree with its own filters.
        this.patients = patients.map((p) => ({
          ...p,
          sub: subLine(p),
          code: codeStatus(p),
          pending: Number(p.pending_count) || 0,
        }));
      } catch (e) {
        console.error("[directives] patient list load failed", e);
        this.$swal({ type: "error", title: "Error", text: "Failed to load patients." });
      } finally {
        this.loading = false;
        this.syncPagination();
      }
    },
    color(p) {
      return CODE_COLORS[p.code.key];
    },
    /** True when the patient holds an ACTIVE record past its re-auth deadline. */
    reauthExpired(p) {
      return String(p && p.reauth_expired) === "1";
    },
    openPatient(p) {
      this.quick = null;
      this.$router.push("/directives/" + p.patient_pid);
    },
  },
};
</script>

<style scoped>
/* The same off-white ground the detail page paints. Without it App.vue's
   `body { background-color: #cde4ff !important }` — which only applies under
   576px — shows through, so the list read blue on a phone while the detail
   page read off-white. */
.dir-page.l1 { min-height: 100vh; background: var(--eh-paper); }
.l1 { font-size: 16px; line-height: 1.5; padding-bottom: 5rem; }
.l1-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; padding: 1.25rem 0 1rem; }
.l1-head h1 { margin: 0 0 .2rem; font-size: 1.6rem; }
.l1-head p { margin: 0; color: var(--eh-muted); font-size: .9rem; }
.l1-head-right { display: flex; align-items: center; gap: .6rem; flex-wrap: wrap; }
.l1-legend-toggle { border: 1px solid var(--eh-divider); background: var(--eh-white); border-radius: 6px; padding: .55rem .9rem; font-size: .85rem; cursor: pointer; }
.l1-counts { display: flex; gap: .5rem; flex-wrap: wrap; }
.l1-count { background: var(--eh-muted-fill); border-radius: 999px; padding: .35rem .8rem; font-size: .8rem; color: var(--eh-muted); }
.l1-count--warn { background: color-mix(in srgb, var(--eh-amber) 18%, var(--eh-white)); color: var(--eh-teal-ink); }

.l1-legend { display: flex; gap: 1.25rem; flex-wrap: wrap; align-items: flex-start; background: var(--eh-paper); border: 1px solid var(--eh-divider); border-radius: 8px; padding: .9rem 1rem; margin-bottom: .9rem; }
.l1-legend-item { display: flex; gap: .5rem; align-items: flex-start; }
.l1-legend-dot { width: 12px; height: 12px; border-radius: 3px; margin-top: .28rem; flex: none; }
.l1-legend-name { font-weight: 600; font-size: .88rem; }
.l1-legend-desc { color: var(--eh-muted); font-size: .8rem; }
.l1-legend-note { flex: 1 1 100%; margin: .35rem 0 0; color: var(--eh-muted); font-size: .8rem; }

.l1-tools { display: flex; gap: .75rem; flex-wrap: wrap; align-items: center; margin-bottom: .75rem; }
.l1-search { flex: 1 1 260px; padding: .6rem .8rem; border-radius: 8px; font-size: .92rem; }
.l1-filters-label { flex: none; font-size: .68rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--eh-muted); }
.l1-filters { display: flex; gap: .4rem; flex-wrap: wrap; }
.l1-chip { display: inline-flex; align-items: center; gap: .4rem; border: 1px solid var(--eh-divider); background: var(--eh-white); border-radius: 999px; padding: .45rem .8rem; font-size: .82rem; cursor: pointer; }
.l1-chip.on { border-color: var(--eh-teal); background: var(--eh-teal); color: var(--eh-on-dark); }
.l1-dot { width: 8px; height: 8px; border-radius: 50%; }

.l1-table { border-top: 1px solid var(--eh-divider); }
.l1-row { display: grid; grid-template-columns: 200px minmax(180px, 1fr) minmax(220px, 1.4fr) 190px 24px; gap: 1rem; align-items: center; padding: .75rem .25rem; border-bottom: 1px solid var(--eh-divider); }
.l1-row--head { font-size: .68rem; letter-spacing: .08em; text-transform: uppercase; padding: .5rem .25rem; }
.l1-row--data { cursor: pointer; }
.l1-row--data:hover, .l1-row--data:focus-visible { background: var(--eh-teal-wash); outline: none; }
.l1-cell { min-width: 0; display: flex; flex-direction: column; gap: .1rem; }
/* "CARE LIMITATIONS" is long — let it breathe rather than clip. */
.l1-status { display: block; text-align: center; font-weight: 800; font-size: .95rem; line-height: 1.15; padding: .5rem .35rem; border-radius: 4px; letter-spacing: .01em; }
.l1-pendflag { margin-top: .3rem; font-size: .72rem; color: var(--eh-teal-ink); background: color-mix(in srgb, var(--eh-amber) 18%, var(--eh-white)); border-radius: 3px; padding: .12rem .4rem; text-align: center; }
.l1-reauth { margin-top: .3rem; font-size: .72rem; font-weight: 600; color: var(--eh-copper-deep); background: var(--eh-copper-wash); border: 1px solid var(--eh-copper); border-radius: 3px; padding: .12rem .4rem; text-align: center; white-space: nowrap; }
.l1-cell--who b { font-size: 1rem; }
.l1-cell--who small, .l1-cell--by small { color: var(--eh-muted); font-size: .78rem; }
.l1-cell--force em, .l1-cell--by em { font-style: normal; color: var(--eh-muted); font-size: .86rem; }
.l1-quick { align-self: flex-start; border-radius: 6px; padding: .4rem .7rem; font-size: .85rem; cursor: pointer; }
.l1-cell--go { text-align: right; color: var(--eh-muted); font-size: 1.3rem; }
.l1-none { padding: 2rem .25rem; color: var(--eh-muted); }

.dir-empty { text-align: center; padding: 3rem 1rem; color: var(--eh-muted); }

.l1-pager { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; padding: 1rem .25rem 0; }
.l1-pager-info { color: var(--eh-muted); font-size: .85rem; }

.l1-scrim { position: fixed; inset: 0; background: rgba(10, 14, 18, .55); display: flex; align-items: center; justify-content: center; z-index: 900; padding: 1rem; }
.l1-sheet { background: var(--eh-white); border-radius: 10px; width: min(560px, 100%); max-height: 86vh; overflow: auto; box-shadow: 0 24px 60px rgba(0,0,0,.35); }
.l1-sheet-top { display: flex; justify-content: space-between; align-items: flex-start; padding: 1.1rem 1.25rem; }
.l1-sheet-code { margin: 0; font-size: 1.7rem; font-weight: 900; letter-spacing: -.01em; }
.l1-sheet-who { margin: .15rem 0 0; opacity: .9; font-size: .9rem; }
.l1-close { background: rgba(255,255,255,.2); border: 0; color: inherit; font-size: 1.4rem; line-height: 1; width: 32px; height: 32px; border-radius: 50%; cursor: pointer; }
.l1-sheet-list { list-style: none; margin: 0; padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: .9rem; }
.l1-sheet-list li { display: flex; flex-direction: column; border-left: 3px solid var(--eh-divider); padding-left: .75rem; }
.l1-sheet-list b { font-size: 1.05rem; }
.l1-sheet-foot { display: flex; justify-content: space-between; align-items: center; gap: .75rem; flex-wrap: wrap; border-top: 1px solid var(--eh-divider); padding: .9rem 1.25rem; font-size: .8rem; color: var(--eh-muted); }
.l1-open { border: 0; border-radius: 6px; padding: .55rem .9rem; font-size: .85rem; cursor: pointer; }

/* Mobile: the grid becomes stacked cards with the status as a full-width band. */
@media (max-width: 900px) {
  .l1-row--head { display: none; }
  .l1-row { grid-template-columns: 1fr; gap: .5rem; padding: .9rem .25rem; border-bottom: 8px solid var(--eh-muted-fill); }
  .l1-cell--go { display: none; }
  .l1-status { font-size: 1.15rem; padding: .65rem; }
  .l1-cell--by { flex-direction: row; gap: .4rem; align-items: baseline; flex-wrap: wrap; font-size: .8rem; }
  .l1-quick { align-self: stretch; text-align: center; padding: .6rem; }
  .l1-scrim { align-items: flex-end; padding: 0; }
  .l1-sheet { width: 100%; border-radius: 14px 14px 0 0; max-height: 90vh; }
}

/* Phone. Six filter chips wrapped onto three rows and pushed the patient list
   below the fold; a single swipeable row keeps them one line. */
@media (max-width: 600px) {
  .l1-filters {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: .25rem;
  }
  .l1-filters::-webkit-scrollbar { display: none; }
  .l1-chip { flex: none; min-height: 44px; }
  .l1-search, .l1-legend-toggle { min-height: 44px; }
}
</style>
